import { createFileRoute, redirect } from '@tanstack/react-router';
import { useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import type { SubscriptionTier } from '@/types/subscription';
import styles from './checkout.module.scss';

// Load Stripe outside of component to avoid recreating on every render
const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || ''
);

export const Route = createFileRoute('/checkout')({
  component: CheckoutPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      tier: (search.tier as SubscriptionTier) || 'deep_dive',
    };
  },
  beforeLoad: async ({ context }) => {
    // Check authentication before loading the checkout page
    const { user, authLoading } = context as { user: any; authLoading: boolean };
    if (authLoading) return;
    if (!user) {
      throw redirect({
        to: '/',
      });
    }
  },
});

function CheckoutPage() {
  const { user } = useAuth();
  const { tier } = Route.useSearch();

  // Callback to fetch client secret for embedded checkout
  const fetchClientSecret = useCallback(async () => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      const { clientSecret } = await api.createCheckoutSession({ tier });

      if (!clientSecret) {
        throw new Error('Failed to create checkout session');
      }

      return clientSecret;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  }, [tier, user]);

  // Options for embedded checkout
  const options = { fetchClientSecret };

  // This should never happen due to beforeLoad guard, but keep as safety
  if (!user) {
    return null;
  }

  return (
    <div className={styles.checkout}>
      <div className={styles.checkout__container}>
        <h2 className={styles.checkout__title}>Complete Your Subscription</h2>
        <p className={styles.checkout__subtitle}>
          You're upgrading to the {tier === 'deep_dive' ? 'Deep Dive' : 'Mastermind'} plan
        </p>

        <div className={styles.checkout__form}>
          <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </div>
      </div>
    </div>
  );
}
